// src/components/DaySpellsStrip.jsx
//
// Today's day, spell by spell: every stretch at the desk and every break, for
// everybody, with the times they ran.
//
// The Clock Status panel above says who is in and who is on lunch right now.
// The card above that says "Break Hours 00:00:00". Neither can say when
// somebody went or how long they were gone, and a single break figure cannot
// tell an hour of lunch from six five-minute breaks — which is the difference
// between a normal day and one worth asking about.
//
// A row per spell rather than a total per person, because the shape of the day
// is the point: three Actives with two breaks between them reads as a normal
// morning, and one Active running since 10:43 reads as somebody who has not
// stopped.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { punchSpellsApis } from "../redux/dashboard/dashboardActions";
import "../assets/css/daySpells.css";

/* The work-status menu's own words, so the table and the menu agree. */
const TONE = {
  Active: "green",
  Lunch: "orange",
  Break: "blue",
  "Be right back": "grey",
};

const ICON = {
  Active: "bi-laptop",
  Lunch: "bi-cup-hot",
  Break: "bi-pause-circle",
  "Be right back": "bi-arrow-left-right",
};

const timeOf = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const DaySpellsStrip = () => {
  const dispatch = useDispatch();
  const [spells, setSpells] = useState([]);
  const [work, setWork] = useState([]);
  const [failed, setFailed] = useState(false);
  /* Totals first. The spell list is the evidence; the per-person totals are
     what somebody actually came to read — "how much lunch did the team take"
     is a question about a number, and answering it from 69 rows means adding
     them up by eye. The detail is one click away and unchanged. */
  const [view, setView] = useState("totals");

  useEffect(() => {
    let alive = true;

    const load = () =>
      dispatch(punchSpellsApis({ scope: "team" }))
        .unwrap()
        .then((res) => {
          if (!alive) return;
          const body = res?.data ?? res;
          const inner = body?.data ?? body;
          /* The endpoint returns { spells, work }. Still tolerates a bare
             array, so an older API build shows the spells and simply has no
             Idle column rather than an empty panel. */
          setSpells(Array.isArray(inner) ? inner : inner?.spells ?? []);
          setWork(Array.isArray(inner) ? [] : inner?.work ?? []);
          setFailed(false);
        })
        .catch(() => {
          if (!alive) return;
          setSpells([]);
          setWork([]);
          setFailed(true);
        });

    load();
    /* A spell that is still running grows while you look at it, and this is
       read to see who is still on lunch. Same cadence as the running-task
       panel below it. */
    const id = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [dispatch]);

  /* Server order is already name, then time — which is the order the day
     reads in. Kept rather than re-sorted here so one person's spells stay
     together and in sequence. */
  const rows = useMemo(() => spells, [spells]);

  const totals = useMemo(() => {
    const t = {};
    rows.forEach((r) => {
      t[r.Reason] = (t[r.Reason] || 0) + (Number(r.Seconds) || 0);
    });
    return t;
  }, [rows]);

  /* One row per person, one column per reason — the cumulative time each of
     them spent in each state today.
     *
     * A pivot rather than a long list of name/reason pairs: the reasons are a
     * fixed set of four, so as columns they line up down the table and one
     * person can be read against another. As rows they would triple the
     * height and put Anbukathir's lunch nowhere near Arjunan's. */
  const byPerson = useMemo(() => {
    /* What each person's desk time was actually spent on, keyed for lookup. */
    const workBy = new Map(work.map((w) => [w.UserCode, w]));

    const people = new Map();

    rows.forEach((r) => {
      if (!people.has(r.UserCode)) {
        people.set(r.UserCode, {
          UserCode: r.UserCode,
          UserName: r.UserName,
          seconds: {},
          spells: {},
          total: 0,
          open: false,
        });
      }
      const p = people.get(r.UserCode);
      const sec = Number(r.Seconds) || 0;
      p.seconds[r.Reason] = (p.seconds[r.Reason] || 0) + sec;
      p.spells[r.Reason] = (p.spells[r.Reason] || 0) + 1;
      p.total += sec;
      if (r.IsOpen) p.open = true;
    });

    /* Idle, Utilisation and Efficiency — all three from Active against task
       time, and all three deliberately arithmetic the reader can check:

         Idle        = Active − Task. Time at the desk that no task was open
                       for. Floored at zero, because a task open while
                       somebody is clocked out would otherwise make the
                       column negative and read as a fault rather than as the
                       double-counting it is.

         Utilisation = Task ÷ Active. How much of the time present went into
                       a task. Against ACTIVE rather than against the whole
                       day, so a long lunch does not count as poor
                       utilisation — that is what the Lunch column is for.

         Efficiency  = Estimate ÷ Task, on tasks CLOSED today. Over 100 means
                       the work came in under its estimate. Blank where
                       nothing closed: an efficiency against no finished work
                       is a number about nothing. */
    return [...people.values()]
      .map((p) => {
        const w = workBy.get(p.UserCode);
        const active = p.seconds.Active || 0;
        const task = Math.min(Number(w?.TaskSeconds) || 0, active || Infinity);
        const estimate = Number(w?.EstimateSeconds) || 0;
        const closed = Number(w?.TasksClosed) || 0;
        const touched = Number(w?.TasksTouched) || 0;

        return {
          ...p,
          task,
          idle: Math.max(0, active - task),
          utilisation: active > 0 ? Math.round((task / active) * 100) : null,
          /* Shown whenever there is an estimate to measure against, not only
             when something finished. Requiring a completion was the other
             half of the old fault: a developer who worked all day on tasks
             still in progress had a real estimate and a real elapsed time,
             and the column printed a dash. */
          efficiency: estimate > 0 && task > 0
            ? Math.round((estimate / task) * 100)
            : null,
          closed,
          touched,
        };
      })
      .sort((a, b) => a.UserName.localeCompare(b.UserName));
  }, [rows, work]);

  const hhmm = (sec) => {
    const m = Math.floor(sec / 60);
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };

  return (
    <div className="dsp">
      <div className="dsp__head">
        <span className="dsp__title">
          <i className="bi bi-clock-history" />
          Today&apos;s Spells
        </span>

        {/* The day's shape in one line, so the totals are readable without
            adding up the rows underneath. */}
        <span className="dsp__totals">
          {Object.keys(TONE)
            .filter((k) => totals[k])
            .map((k) => (
              <span className={`dsp__pill dsp__pill--${TONE[k]}`} key={k}>
                {k}
                <b>{hhmm(totals[k])}</b>
              </span>
            ))}
          {rows.length > 0 && (
            <span className="dsp__pill">
              Spells<b>{rows.length}</b>
            </span>
          )}

          <span className="dsp__views">
            {[
              { key: "totals", label: "Totals", icon: "bi-bar-chart-line" },
              { key: "spells", label: "Detail", icon: "bi-list-ul" },
            ].map((v) => (
              <button
                key={v.key}
                type="button"
                className={`dsp__view${view === v.key ? " is-active" : ""}`}
                onClick={() => setView(v.key)}
              >
                <i className={`bi ${v.icon}`} />
                {v.label}
              </button>
            ))}
          </span>
        </span>
      </div>

      <div className="dsp__scroll">
        {view === "totals" ? (
          <table className="dsp__table">
            <thead>
              <tr>
                <th>Name</th>
                {Object.keys(TONE).map((k) => (
                  <th className="dsp__num" key={k}>
                    {k}
                  </th>
                ))}
                <th className="dsp__num">Total</th>
                <th
                  className="dsp__num"
                  title="Time on tasks, from the query status tracker"
                >
                  Working
                </th>
                <th className="dsp__num" title="Time at the desk with no task open">
                  Idle
                </th>
                <th className="dsp__num" title="Task time as a share of Active">
                  Util.
                </th>
                <th
                  className="dsp__num"
                  title="Estimate against time spent, on tasks closed today"
                >
                  Effic.
                </th>
              </tr>
            </thead>
            <tbody>
              {byPerson.length === 0 && (
                <tr>
                  <td colSpan={Object.keys(TONE).length + 6} className="dsp__empty">
                    {failed
                      ? "Could not read today's punches."
                      : "No punches recorded yet today."}
                  </td>
                </tr>
              )}

              {byPerson.map((p) => (
                <tr className="dsp__row" key={p.UserCode}>
                  <td>
                    {p.UserName}
                    {p.open && <span className="dsp__dot" title="Still running" />}
                  </td>
                  {Object.keys(TONE).map((k) => (
                    <td className="dsp__num" key={k}>
                      {p.seconds[k] ? (
                        <>
                          {hhmm(p.seconds[k])}
                          {p.spells[k] > 1 && (
                            <span className="dsp__times-count">
                              {p.spells[k]}×
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="dsp__zero">—</span>
                      )}
                    </td>
                  ))}
                  <td className="dsp__num dsp__total">{hhmm(p.total)}</td>

                  {/* The figure Util. and Effic. are built from. Shown rather
                      than left implicit: two percentages whose inputs are
                      nowhere on the table are two numbers nobody can check,
                      and this is the one the reader is most likely to
                      dispute. */}
                  <td className="dsp__num dsp__working">
                    {p.task > 0 ? hhmm(p.task) : <span className="dsp__zero">—</span>}
                  </td>

                  <td className="dsp__num">
                    {p.idle > 0 ? hhmm(p.idle) : <span className="dsp__zero">—</span>}
                  </td>

                  <td className="dsp__num">
                    {p.utilisation === null ? (
                      <span className="dsp__zero">—</span>
                    ) : (
                      <span className={`dsp__pct${p.utilisation < 50 ? " is-low" : ""}`}>
                        {p.utilisation}%
                      </span>
                    )}
                  </td>

                  <td className="dsp__num">
                    {p.efficiency === null ? (
                      /* Nothing closed today, so there is nothing to measure
                         an estimate against. */
                      <span className="dsp__zero" title="No estimate on today's tasks">
                        —
                      </span>
                    ) : (
                      <span
                        className={`dsp__pct${p.efficiency >= 100 ? " is-good" : " is-low"}`}
                        title={`${p.touched} task${p.touched === 1 ? "" : "s"}, ${p.closed} closed`}
                      >
                        {p.efficiency}%
                      </span>
                    )}
                  </td>
                </tr>
              ))}

              {byPerson.length > 0 && (
                <tr className="dsp__row dsp__grand">
                  <td>All ({byPerson.length})</td>
                  {Object.keys(TONE).map((k) => (
                    <td className="dsp__num" key={k}>
                      {totals[k] ? hhmm(totals[k]) : "—"}
                    </td>
                  ))}
                  <td className="dsp__num dsp__total">
                    {hhmm(Object.values(totals).reduce((n, v) => n + v, 0))}
                  </td>
                  <td className="dsp__num dsp__working">
                    {hhmm(byPerson.reduce((n, p) => n + p.task, 0))}
                  </td>
                  <td className="dsp__num">
                    {hhmm(byPerson.reduce((n, p) => n + p.idle, 0))}
                  </td>
                  {/* The team's utilisation is total task time over total
                      active time — not the average of the percentages, which
                      would weigh a two-hour day the same as an eight. */}
                  <td className="dsp__num">
                    {totals.Active
                      ? `${Math.round(
                          (byPerson.reduce((n, p) => n + p.task, 0) /
                            totals.Active) *
                            100,
                        )}%`
                      : "—"}
                  </td>
                  <td className="dsp__num">—</td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
        <table className="dsp__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Reason</th>
              <th>From</th>
              <th>To</th>
              <th className="dsp__num">Duration</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="dsp__empty">
                  {failed
                    ? "Could not read today's punches."
                    : "No punches recorded yet today."}
                </td>
              </tr>
            )}

            {rows.map((r, i) => (
              <tr
                key={`${r.UserCode}-${r.FromTime}-${i}`}
                /* A person's block is separated from the next by a rule
                   rather than a repeated heading: the name is on every row
                   already, and a group header would double the height of a
                   table whose whole value is being short. */
                className={
                  i > 0 && rows[i - 1].UserCode !== r.UserCode
                    ? "dsp__row dsp__row--newperson"
                    : "dsp__row"
                }
              >
                <td>{r.UserName}</td>
                <td>
                  <span className={`dsp__tag dsp__tag--${TONE[r.Reason] || "grey"}`}>
                    <i className={`bi ${ICON[r.Reason] || "bi-dot"}`} />
                    {r.Reason}
                  </span>
                </td>
                <td>{timeOf(r.FromTime) || "--:--"}</td>
                <td>
                  {r.IsOpen ? (
                    <span className="dsp__open">(open)</span>
                  ) : (
                    timeOf(r.ToTime) || "--:--"
                  )}
                </td>
                <td className="dsp__num">{r.HHMM}</td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
};

export default DaySpellsStrip;
